import React from 'react'
import { createRoot } from 'react-dom/client'
import App from './App.tsx'
import './index.css'

// Dev-only: cleanup any old or dev service workers and caches to avoid update loops
if (import.meta.env.DEV && 'serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((regs) => {
    regs.forEach((reg) => {
      const scriptURL =
        reg.active?.scriptURL || reg.installing?.scriptURL || reg.waiting?.scriptURL || '';
      if (scriptURL.includes('dev-sw') || scriptURL.endsWith('/sw.js')) {
        reg.unregister().catch(() => {});
      }
    });
  });
  // Clear common caches used by workbox/runtime
  if (typeof caches !== 'undefined') {
    caches.keys().then((keys) => {
      keys
        .filter((k) => k.startsWith('workbox-') || k === 'supabase-cache' || k === 'api-cache')
        .forEach((k) => caches.delete(k));
    });
  }
  sessionStorage.removeItem('pwa-update-dismissed');
}

// Simple recovery fallback component
const SimpleRecovery = () => (
  <div style={{
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #dc2626, #000, #dc2626)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    padding: '2rem'
  }}>
    <div style={{
      textAlign: 'center',
      padding: '2rem',
      background: 'rgba(0,0,0,0.3)',
      borderRadius: '12px',
      backdropFilter: 'blur(10px)',
      maxWidth: '500px',
      width: '100%'
    }}>
      <h1 style={{ margin: '0 0 1rem 0', fontSize: '1.5rem' }}>PropertyShield AI</h1>
      <p style={{ margin: '0 0 1.5rem 0', opacity: 0.9 }}>Application temporarily unavailable</p>
      <button 
        onClick={() => {
          localStorage.clear();
          sessionStorage.clear();
          window.location.href = '/operations';
        }}
        style={{
          padding: '0.75rem 1.5rem',
          background: '#dc2626',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer',
          fontWeight: '500'
        }}
      >
        Go to Dashboard
      </button>
    </div>
  </div>
);

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Root element not found");
}

const root = createRoot(rootElement);

// Enhanced root rendering with recovery fallback
try {
  root.render(<App />);
} catch (renderError) {
  console.error('Critical render error:', renderError);
  
  // If main app fails, try simple recovery
  try {
    root.render(<SimpleRecovery />);
  } catch (recoveryError) {
    console.error('Recovery component also failed:', recoveryError);
    
    // Final fallback: XSS-safe direct DOM manipulation using createElement
    const container = document.createElement('div');
    container.style.cssText = 'display: flex; align-items: center; justify-content: center; min-height: 100vh; background: linear-gradient(135deg, #dc2626, #000, #dc2626); color: white; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding: 2rem;';
    
    const card = document.createElement('div');
    card.style.cssText = 'text-align: center; padding: 2rem; background: rgba(0,0,0,0.3); border-radius: 12px; backdrop-filter: blur(10px); max-width: 500px; width: 100%;';
    
    const title = document.createElement('h1');
    title.style.cssText = 'margin: 0 0 1rem 0; font-size: 1.5rem;';
    title.textContent = 'PropertyShield AI';
    card.appendChild(title);
    
    const message = document.createElement('p');
    message.style.cssText = 'margin: 0 0 1.5rem 0; opacity: 0.9;';
    message.textContent = 'Application temporarily unavailable';
    card.appendChild(message);
    
    const dashboardBtn = document.createElement('button');
    dashboardBtn.style.cssText = 'padding: 0.75rem 1.5rem; background: #dc2626; color: white; border: none; border-radius: 6px; cursor: pointer; font-weight: 500; margin-right: 0.5rem;';
    dashboardBtn.textContent = 'Go to Dashboard';
    dashboardBtn.onclick = () => { window.location.href = '/operations'; };
    card.appendChild(dashboardBtn);
    
    const refreshBtn = document.createElement('button');
    refreshBtn.style.cssText = 'padding: 0.75rem 1.5rem; background: transparent; color: white; border: 1px solid rgba(255,255,255,0.3); border-radius: 6px; cursor: pointer; font-weight: 500;';
    refreshBtn.textContent = 'Refresh';
    refreshBtn.onclick = () => { window.location.reload(); };
    card.appendChild(refreshBtn);
    
    container.appendChild(card);
    
    // Clear and append safely
    while (rootElement.firstChild) {
      rootElement.removeChild(rootElement.firstChild);
    }
    rootElement.appendChild(container);
  }
}
